// A plain folder on a person's page: Photos, Miscellaneous.
//
// No records here — these two hold loose files and nothing else, so this is
// the ordinary document list with the folder fixed. It reuses DocumentList and
// UploadBox rather than reimplementing them, so the desktop table and the
// phone cards keep behaving the way they do everywhere else in the product.
//
// No expiry box: a date that expires belongs on a RECORD, in one of the two
// folders above, where something will chase it. Offering one here would create
// dates that look tracked and are not.
import { useState } from 'react';
import { ApiError } from '../../api';
import { documents, type Attachment } from '../../documentsApi';
import type { PersonSection } from '../../lib/personSections';
import { DocumentList, UploadBox } from '../Documents';
import { DocumentViewer } from '../DocumentViewer';

export function FolderSection({
  section,
  resourceId,
  files,
  canEdit,
  onChanged,
  onError,
}: {
  section: PersonSection;
  resourceId: string;
  files: Attachment[];
  canEdit: boolean;
  onChanged: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [viewing, setViewing] = useState<Attachment | null>(null);

  async function remove(file: Attachment) {
    if (!window.confirm(`Remove “${file.title}”? An admin can get it back for 30 days.`)) return;
    try {
      await documents.remove(file.id);
      onChanged('Removed.');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not remove that.');
    }
  }

  return (
    <section className="panel">
      <div className="row mb-3">
        <h2 className="!mb-0">
          {section.label}
          {files.length > 0 && <span className="muted"> ({files.length})</span>}
        </h2>
      </div>

      {canEdit && (
        <UploadBox
          kind="resource"
          ownerId={resourceId}
          allowExpiry={false}
          photosOnly={false}
          category={section.attachmentCategory}
          hint={
            section.key === 'photos'
              ? 'Photos of this person: a site ID shot, a photo of a card. They open in the app.'
              : 'Anything that does not belong in the folders above.'
          }
          onUploaded={() => onChanged('Uploaded.')}
          onError={onError}
        />
      )}

      <DocumentList
        documents={files}
        emptyMessage={section.empty ?? 'Nothing here yet.'}
        onOpen={setViewing}
        onRemove={remove}
        canRemove={() => canEdit}
      />

      {viewing && <DocumentViewer attachment={viewing} onClose={() => setViewing(null)} />}
    </section>
  );
}
